import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import { NumericFormat } from "react-number-format";
import TextField from "@mui/material/TextField";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import FormControl from "@mui/material/FormControl";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import UserInterface from "@homegame/common/dist/interface/user.interface";
import GameInterface from "@homegame/common/dist/interface/game.interface";
import { useAuth } from "../context/auth.context";
import SelectUserComponent from "./select-user.component";
import { Controller, useForm } from "react-hook-form";
import TransactionStatus from "@homegame/common/dist/enum/transaction-status.enum";
import InputAdornment from "@mui/material/InputAdornment";
import { useToast } from "../hooks/use-toast.hook";
import formatCurrency from "../helpers/format-currency.helper";
import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      color: "#fff",
      fontWeight: 300,
      textAlign: "center",
    },
    form: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      padding: "10px 27px 15px 27px",
    },
    btn: { width: 200, marginTop: 15 },
    btnAccept: { width: 200, marginTop: 15, backgroundColor: "green" },
    moneyInput: {
      color: "#fff",
      "& .MuiTypography-root, & .Mui-disabled": {
        color: "#fff !important",
        "-webkit-text-fill-color": "#fff",
      },
    },
    errorText: {
      backgroundColor: "red",
      color: "#fff !important",
      fontSize: 12,
      textShadow: "none",
      padding: "5px 15px",
      borderRadius: 5,
      marginLeft: 0,
    },
    valueText: {
      color: "#ddd",
      fontSize: 12,
    },
  }),
);

/**
 * TODO: assign debt (debts) to player that lost. create cashout on summ based on total debts amount. finish game. when calculating debbts DO NOT create a new debts but use previous logic for next calculations.
 */

/**
 *
 * @param onClose
 * @param game
 * @param toUser
 * @param open
 * @param fromUser
 * @param amount
 * @constructor
 */
export default function AssignDebtComponent({
  onClose,
  game,
  open = true,
  fromUser,
  cashoutFromUser,
  amount = 0,
  onAssign,
  maxAmount,
  initialValue,
}: {
  onClose?: () => any;
  game?: GameInterface;
  fromUser?: UserInterface;
  cashoutFromUser?: UserInterface;
  onAssign: (debt: Partial<TransactionInterface>) => void;
  initialValue?: Partial<TransactionInterface>;
  maxAmount?: number;
  open?: boolean;
  amount?: number;
}) {
  const classes: any = useStyles({});
  const { showToast } = useToast();
  const { user } = useAuth();

  let defaultChipsAmount = "";

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      // @ts-ignore
      fromUser: fromUser ?? null,
      toUser: null,
      chips: defaultChipsAmount,
      amount:
        amount > 0
          ? String(amount)
          : game
            ? String(
                Number(defaultChipsAmount) * (game.moneyValue / game.chipValue),
              )
            : defaultChipsAmount,
      tableId: game?.tableId,
      gameId: game?.id,
      status: TransactionStatus.NOT_SENT,
    },
    values:
      initialValue && Object.keys(initialValue).length
        ? { ...initialValue, fromUser }
        : {
            fromUser: fromUser,
            toUser: null,
            chips: defaultChipsAmount,
            amount:
              amount > 0
                ? String(amount)
                : game
                  ? String(
                      Number(defaultChipsAmount) *
                        (game.moneyValue / game.chipValue),
                    )
                  : defaultChipsAmount,
            tableId: game?.tableId,
            gameId: game?.id,
            status: TransactionStatus.NOT_SENT,
          },
  });

  const onSubmit = async (data: any) => {
    if (!game) return;
    try {
      if (Number(data.amount) < 0) {
        showToast("Amount must be greater than 0!", "warning");
        return;
      }
      if (maxAmount && Number(data.amount) > maxAmount) {
        showToast(`Amount cannot exceed ${maxAmount}`, "warning");
        return;
      }
      onAssign({ ...data, currency: "USD" });
      reset();
      handleClose();
    } catch (e: any) {
      const { message } = e as Error;
      showToast(message, "error");
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  if (!user || !game) return <p>Error</p>;

  const moneyValueValidate = (value: string | number | undefined) => {
    if (!game) return true;

    if (value === undefined || value === null || value === "") {
      return "Wrong value";
    }

    const numValue = typeof value === "number" ? value : parseInt(value);

    if (isNaN(numValue)) return "Wrong value";

    return true;
  };

  const getMoneyFieldLabel = () => {
    return `Money player received`;
  };

  return (
    <Dialog
      fullWidth
      onClose={handleClose}
      aria-labelledby="invite-dialog-title"
      open={open}
    >
      <DialogTitle id="invite-dialog-title" className={classes.title}>
        Assign debt
      </DialogTitle>

      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <FormControl
          fullWidth
          style={{ display: !!fromUser ? "none" : "inline-block" }}
        >
          <Controller
            render={({ field: { ref, ...rest } }) => (
              <SelectUserComponent
                label="From player"
                {...rest}
                disabled={!!fromUser || !game}
                list={[fromUser!]}
              />
            )}
            name="fromUser"
            control={control}
            rules={{ required: "Field required" }}
          />
          {errors.fromUser && (
            <FormHelperText className={classes.errorText} error>
              {errors.fromUser.message}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth className={classes.moneyInput}>
          <Controller
            render={({ field }) => (
              <NumericFormat
                value={field.value}
                onValueChange={(values) => field.onChange(values.value)}
                customInput={TextField}
                fullWidth
                label={getMoneyFieldLabel()}
                allowNegative={true}
                allowLeadingZeros={true}
                fixedDecimalScale={true}
                variant="filled"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {formatCurrency(0, game?.currency, false)}
                    </InputAdornment>
                  ),
                }}
              />
            )}
            name="amount"
            control={control}
            rules={{
              required: "Field required",
              validate: moneyValueValidate,
            }}
          />

          {errors.amount && (
            <FormHelperText className={classes.errorText} error>
              {errors.amount.message}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth style={{ display: "inline-block" }}>
          <Controller
            render={({ field: { ref, ...rest } }) => (
              <SelectUserComponent
                label="To player"
                {...rest}
                list={
                  game.players
                    ? game.players
                        .filter((p) => p.playerId !== cashoutFromUser?.id)
                        .filter((p) => {
                          const hasCashOut = game.transactions?.some(
                            (t) =>
                              t.toUserId === p.playerId &&
                              t.type === TransactionType.CASH_OUT,
                          );

                          return !hasCashOut;
                        })
                        .map((gp) => gp.player!)
                    : []
                }
              />
            )}
            name="toUser"
            control={control}
            rules={{ required: "Field required" }}
          />
          {/*// @ts-ignore*/}
          {errors.toUser && (
            <FormHelperText className={classes.errorText} error>
              {errors.toUser.message}
            </FormHelperText>
          )}
        </FormControl>

        <Button className={classes.btnAccept} variant="contained" type="submit">
          Save Assigned Debts
        </Button>

        <Button
          className={classes.btn}
          onClick={handleClose}
          variant="contained"
          color="secondary"
        >
          Close
        </Button>
      </form>
    </Dialog>
  );
}
